import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for managing zoom functionality - DISABLED FOR NOW
 * Currently returns fixed zoom of 1.0 and basic position management
 * 
 * @param {number} initialZoom - Initial zoom value (default 1.0 = 100%) - IGNORED, FIXED AT 1.0
 * @param {number} minZoom - Minimum zoom value (default 0.5 = 50%) - IGNORED
 * @param {number} maxZoom - Maximum zoom value (default 2.0 = 200%) - IGNORED
 * @param {number} step - Zoom step increment/decrement (default 0.2 = 20%) - IGNORED
 * @returns {Object} Zoom state and handlers
 */
const useZoom = (
  initialZoom = 1.0,
  minZoom = 0.5,
  maxZoom = 2.0,
  step = 0.2
) => {
  // Fixed zoom level of 1.0
  const zoom = 1.0;
  
  // State for position
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  // Refs to track container and image dimensions
  const containerRef = useRef({ width: 0, height: 0 });
  const imageRef = useRef({ width: 0, height: 0 });
  
  // Refs to track the relative position as a percentage of viewport
  const relativePositionRef = useRef({ xPercent: 0.5, yPercent: 0.5 });
  
  /**
   * Update containerRef dimensions
   */
  const updateContainerDimensions = useCallback(() => {
    if (document.querySelector('.viewer-container')) {
      const containerElement = document.querySelector('.viewer-container');
      containerRef.current = {
        width: containerElement.clientWidth,
        height: containerElement.clientHeight
      };
      return true;
    }
    return false;
  }, []);
  
  /**
   * Calculate and store the relative position as percentages
   */
  const updateRelativePosition = useCallback(() => {
    if (containerRef.current.width === 0 || containerRef.current.height === 0) {
      return false;
    }
    
    // Calculate current viewport center in absolute pixels
    const viewportCenterX = containerRef.current.width / 2 - position.x;
    const viewportCenterY = containerRef.current.height / 2 - position.y;
    
    // Store as percentages of the container size
    relativePositionRef.current = {
      xPercent: viewportCenterX / containerRef.current.width,
      yPercent: viewportCenterY / containerRef.current.height
    };
    
    return true;
  }, [position]);
  
  /**
   * Handle window resize event - maintain the same relative viewport position
   */
  const handleResize = useCallback(() => {
    // Debounce by waiting until resize is "complete"
    const prevContainerSize = { ...containerRef.current };
    
    // Update container dimensions
    const dimensionsUpdated = updateContainerDimensions();
    if (!dimensionsUpdated) return;
    
    // If no significant change in dimensions, don't recalculate
    if (Math.abs(prevContainerSize.width - containerRef.current.width) < 5 &&
        Math.abs(prevContainerSize.height - containerRef.current.height) < 5) {
      return;
    }
    
    // Calculate new position based on the relative position percentages
    const newX = containerRef.current.width * (1 - relativePositionRef.current.xPercent) - 
                 containerRef.current.width / 2;
    const newY = containerRef.current.height * (1 - relativePositionRef.current.yPercent) - 
                 containerRef.current.height / 2;
    
    setPosition({ x: newX, y: newY });
    
    console.log('Window resized, adjusted position:', {
      prevSize: prevContainerSize,
      newSize: containerRef.current,
      relativePosition: relativePositionRef.current,
      newPosition: { x: newX, y: newY }
    });
  }, [updateContainerDimensions]);
  
  // Set up resize event listener
  useEffect(() => {
    // Initialize container dimensions
    updateContainerDimensions();
    
    // Create debounced resize handler
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 200);
    };
    
    // Add event listener
    window.addEventListener('resize', debouncedResize);
    
    // Cleanup
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [updateContainerDimensions, handleResize]);
  
  // Update relative position whenever position changes
  useEffect(() => {
    updateRelativePosition();
  }, [position, updateRelativePosition]);
  
  /**
   * Adjust position to keep the center point fixed during zoom - DISABLED
   */
  const adjustPositionForZoom = useCallback((oldZoom, newZoom, viewportSize) => {
    // Disabled - no zoom adjustment
    return;
  }, []);
  
  /**
   * Increase zoom by step amount - DISABLED
   */
  const zoomIn = useCallback((viewportSize) => {
    // Disabled - zoom is fixed at 1.0
    console.log('Zoom functionality is currently disabled');
    return;
  }, []);
  
  /**
   * Decrease zoom by step amount - DISABLED
   */
  const zoomOut = useCallback((viewportSize) => {
    // Disabled - zoom is fixed at 1.0
    console.log('Zoom functionality is currently disabled');
    return;
  }, []);
  
  /**
   * Reset zoom to initial value - DISABLED
   */
  const resetZoom = useCallback((viewportSize) => {
    // Disabled - zoom is fixed at 1.0
    console.log('Zoom functionality is currently disabled');
    return;
  }, []);
  
  /**
   * Set position directly
   * @param {Object} newPosition - New position {x, y}
   */
  const setPositionDirectly = useCallback((newPosition) => {
    setPosition(newPosition);
  }, []);
  
  return {
    zoom, // Fixed at 1.0
    position,
    zoomIn,
    zoomOut,
    resetZoom,
    setPosition: setPositionDirectly
  };
};

export default useZoom; 